import React, {useState, useCallback, useRef} from 'react';
import {
  _m,
  AuthenticationController,
  CurrentUserStore,
  SKZPersonalInfo,
  TabType,
  AWSManager,
  LoadingIndicator,
  SkillzRouter,
  WebDeepLinkHandler,
} from '@sdk-mobile';
import getGameId from '@/utils/getGameId';
import RegistrationStep from '@/app/(app)/games/[gid]/sign-in/_src/RegistrationStep';

type FieldState = {
  value: string;
  isValid: boolean;
  isFocused: boolean;
  errorMessage?: string;
};

type FormState = {
  firstName: FieldState;
  lastName: FieldState;
  email: FieldState;
  additionalField: FieldState;
};

type Props = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

const noop = () => {};

function isValidEmail(email = '') {
  const atIndex = email.indexOf('@');
  const dotIndex = email.indexOf('.', atIndex);

  return atIndex > 0 && dotIndex > atIndex + 1 && dotIndex < email.length - 1;
}

export default function AdditionalInfo({firstName, lastName, email}: Props) {
  const gameId = getGameId();
  const agreement = useRef<HTMLInputElement | null>(null);
  const [isSmsMarketingChecked, setIsSmsMarketingChecked] = useState(false);
  const onSmsMarketingPress = (isChecked: boolean) => {
    setIsSmsMarketingChecked(isChecked);
  };

  const [formState, setFormState] = useState<FormState>({
    firstName: {
      value: firstName || '',
      isValid: false,
      isFocused: false,
    },
    lastName: {
      value: lastName || '',
      isValid: false,
      isFocused: false,
    },
    email: {
      value: email || '',
      isValid: false,
      isFocused: false,
    },
    additionalField: {
      value: '',
      isValid: false,
      isFocused: false,
    },
  });

  const validateField = (name: keyof FormState, value: string): string | undefined => {
    if (!value.trim()) {
      return _m('This field is required');
    }
    if (value.trim().length < 2) {
      return _m('Minimum 2 characters required');
    }
    if (name === 'email' && !isValidEmail(value)) {
      return _m('Invalid email address');
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: {
        ...prev[name as keyof FormState],
        value,
      },
    }));
  };

  const handleFocus = (name: keyof FormState) => {
    setFormState((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        isFocused: true,
        errorMessage: undefined,
      },
    }));
  };
  const handleBlur = (name: keyof FormState) => {
    setFormState((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        isFocused: false,
      },
    }));
  };

  const validateForm = (): boolean => {
    const newFormState = {...formState};
    let isValid = true;

    (Object.keys(formState) as Array<keyof FormState>).forEach((fieldName) => {
      const errorMessage = validateField(fieldName, formState[fieldName].value);
      newFormState[fieldName] = {
        ...newFormState[fieldName],
        isValid: !errorMessage,
        errorMessage,
      };
      if (errorMessage) {
        isValid = false;
      }
    });

    setFormState(newFormState);
    return isValid;
  };

  const onSendPressed = async () => {
    if (validateForm()) {
      LoadingIndicator.show();
      const personalInfo = await CurrentUserStore.getUserPersonalInfo().catch(noop);
      const newUserPersonal = new SKZPersonalInfo(personalInfo || {});

      newUserPersonal.first_name = formState.firstName.value;
      newUserPersonal.last_name = formState.lastName.value;

      if (formState.email.value !== personalInfo.email) {
        newUserPersonal.email_verified = false;
        newUserPersonal.email = formState.email.value;
      }

      if (isSmsMarketingChecked !== undefined) {
        CurrentUserStore.updateUserProperty('optInStatus', isSmsMarketingChecked.toString());
      }

      try {
        await CurrentUserStore.updateUserPersonalInfo(newUserPersonal);
      } catch (error) {
        console.error('Error updating user personal info', error);
      }

      try {
        await CurrentUserStore.updateUserProperty('company_name', formState.additionalField.value);
      } catch (error) {
        console.error('Error updating user property', error);
      }
      CurrentUserStore.clearUserPersonalCache();

      AWSManager.trackEvent('HOSTED_LIVE_EVENTS_REGISTER_WEB', {
        company: formState.additionalField.value,
        email: formState.email.value,
        emailVerified: newUserPersonal.email_verified,
        promotionalMessages: agreement.current?.checked,
      });
      //moved the logic to WebDeepLinkHandler
      const didHandleDeepLink = await WebDeepLinkHandler.checkAndHandleDeepLink();
      if (!didHandleDeepLink) {
        SkillzRouter.replace(`/games/${gameId}/home`);
      }
      LoadingIndicator.hide();
    }
  };

  const onSignIn = useCallback(
    () =>
      AuthenticationController.displaySignInScreen(
        false,
        undefined,
        undefined,
        undefined,
        'dark',
        TabType.EMAIL
      ),
    []
  );
  return (
    <>
      <RegistrationStep step={2} />
      <div
        className={
          'w-screen grow relative text-white flex flex-col justify-between items-center px-6 pb-8'
        }
      >
        <div className='w-full max-w-[600px] '>
          <div className='flex gap-4 mb-2'>
            <div className='flex-1'>
              <label htmlFor='firstName' className={'text-white font-bold mb-2 block'}>
                {_m('First Name')}
              </label>
              <input
                id='firstName'
                type='text'
                name='firstName'
                value={formState.firstName.value}
                onChange={handleInputChange}
                onFocus={() => handleFocus('firstName')}
                onBlur={() => handleBlur('firstName')}
                className='w-full p-3 rounded-xl bg-transparent border border-[#DDDAD6] text-white'
                autoFocus={true}
              />
              {formState.firstName.errorMessage && !formState.firstName.isFocused ? (
                <p className='text-red-500 text-sm'>{formState.firstName.errorMessage}</p>
              ) : null}
            </div>
            <div className='flex-1'>
              <label htmlFor='lastName' className={'text-white  font-bold mb-2 block'}>
                {_m('Last Name')}
              </label>
              <input
                id='lastName'
                type='text'
                name='lastName'
                value={formState.lastName.value}
                onChange={handleInputChange}
                onFocus={() => handleFocus('lastName')}
                onBlur={() => handleBlur('lastName')}
                className='w-full p-3 rounded-xl bg-transparent border border-[#DDDAD6] text-white'
              />
              {formState.lastName.errorMessage && !formState.lastName.isFocused ? (
                <p className='text-red-500 text-sm'>{formState.lastName.errorMessage}</p>
              ) : null}
            </div>
          </div>

          <div className='mb-2'>
            <label htmlFor='email' className={'text-white  font-bold mb-2 block'}>
              {_m('Email')}
            </label>
            <input
              id='email'
              type='text'
              name='email'
              value={formState.email.value}
              onChange={handleInputChange}
              onFocus={() => handleFocus('email')}
              onBlur={() => handleBlur('email')}
              className='w-full p-3 rounded-xl bg-transparent border border-[#DDDAD6] text-white'
            />
            {formState.email.errorMessage && !formState.email.isFocused ? (
              <p className='text-red-500 text-sm'>{formState.email.errorMessage}</p>
            ) : null}
          </div>

          <div>
            <label htmlFor='additionalField' className={'text-white  font-bold mb-2 block'}>
              {_m('Company Name')}
            </label>
            <input
              id='additionalField'
              type='text'
              name='additionalField'
              value={formState.additionalField.value}
              onChange={handleInputChange}
              onFocus={() => handleFocus('additionalField')}
              onBlur={() => handleBlur('additionalField')}
              className='w-full p-3 rounded-xl bg-transparent border border-[#DDDAD6] text-white'
            />
            {formState.additionalField.errorMessage && !formState.additionalField.isFocused ? (
              <p className='text-red-500 text-sm'>{formState.additionalField.errorMessage}</p>
            ) : null}
          </div>
          <div className='flex gap-2 mt-2'>
            <input
              type='checkbox'
              id='promo-checkbox'
              className='relative peer shrink-0 appearance-none w-6 h-6 border-2 border-blue-500 rounded-sm bg-white mt-1 checked:bg-blue-800 checked:border-0'
              ref={agreement}
              onChange={(event) => onSmsMarketingPress(event.target.checked)} // Converts event to boolean
            />
            <label htmlFor='promo-checkbox' className='text-sm'>
              {_m(
                'I agree to receive promotional messages from Skillz via email, text messages, or other methods. I understand that I can opt out at any time.'
              )}
            </label>
            <svg
              className='absolute w-6 h-6 mt-1 hidden peer-checked:block pointer-events-none'
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              stroke-width='4'
              stroke-linecap='round'
              stroke-linejoin='round'
            >
              <polyline points='20 6 9 17 4 12'></polyline>
            </svg>
          </div>
        </div>

        <div className='w-full'>
          <div
            className={'w-full relative text-white flex flex-col justify-center items-center mb-4'}
          >
            <p className={'text-white text-center text-xs'}>
              {_m('Already have an account?') + ' '}
              <button className={'text-blue-500 font-bold inline text-s'} onClick={onSignIn}>
                {_m('Sign in')}
              </button>
            </p>
          </div>
          <div className={'w-full relative flex flex-col items-center mb-4'}>
            <button
              className={`w-full max-w-[400px] bg-[#308ee2] text-white p-3 rounded-xl font-bold text-lg`}
              onClick={onSendPressed}
            >
              {_m('Next')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
